import { useContext, useEffect, useRef, useState } from "react";
import { Offcanvas } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { GlobalContext } from "../Context/GlobalContext";
import storage from "../Assets/js/Global";
import { imagePath } from "../Url";
import LogOutModal from "../modals/LogOutModal";
import {
  House,
  LogOut,
  AlignLeft,
  ShieldAlert,
  History,
  Files,
  ReceiptText,
  User,
  Info,
  Newspaper,
  Share2,
  Bell,
  ScanFace,
  ChevronLeft,
  X,
  ClipboardList,
  ScanQrCode,
  Bot,
  ShoppingBag,
  HeartPulse,
} from "lucide-react";
import { images } from "../Assets/images";
import { convertUtcToLocal } from "../utils";
import "./Header.css";

const Header = () => {
  const {
    doLogout,
    setIsVerified,
    translatedLabels,
    logoUrl,
    headerLogoUrl,
    setShowExportModal,
    isNotificationReceived,
    handleNotificationClick,
    setIsNotificationReceived,
    getGuestUserList,
    is4Life,
  } = useContext(GlobalContext);
  const isPlanActive = storage.get("isPlanActive");
  const screenLabels = translatedLabels?.common;
  const authLabels = translatedLabels?.auth;
  const user = storage.get("loginObj");
  const route = useLocation();
  const navigate = useNavigate();
  const currentPath = route.pathname;
  const location = useLocation();
  const selectedDate = location.state?.selectedDate;
  const previousPath = useRef(location.pathname);
  const campaign = location.state?.campaignData || location?.state?.campaign;

  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [show, setShow] = useState(false);

  const menuIconRoutes = [
    "/dashboard",
    "/dashboard2",
    "/documents",
    "/articles",
    "/history",
    "/privacy-policy",
    "/terms-condition",
    "/profile",
    "/about-us",
    "/notification",
    "/guest-list",
    "/survey-form",
    "/invites-list",
    "/ask-ai",
    "/charts-screen",
  ];

  const profilePaths = [
    "/profile",
    "/profile-update",
    "/change-password",
    "/deleteAcc-one",
    "/language-selection",
    "/help-guide",
  ];

  const isDashboard = menuIconRoutes.includes(currentPath);
  const isProfileActive = profilePaths.includes(location.pathname);
  const homeRoute = is4Life ? "/dashboard2" : "/dashboard";

  const formatDateTranslated = (timestamp, screenLabels) => {
    if (!timestamp) return "";
    const isNumeric = !isNaN(Number(timestamp));
    const timestampValue = isNumeric ? Number(timestamp) : timestamp;
    const localDate = convertUtcToLocal(timestampValue);
    if (isNaN(localDate)) return "";

    const weekdays = [
      screenLabels?.sun?.label_value || "Sun",
      screenLabels?.mon?.label_value || "Mon",
      screenLabels?.tue?.label_value || "Tue",
      screenLabels?.wed?.label_value || "Wed",
      screenLabels?.thu?.label_value || "Thu",
      screenLabels?.fri?.label_value || "Fri",
      screenLabels?.sat?.label_value || "Sat",
    ];
    const months = [
      screenLabels?.jan?.label_value || "Jan",
      screenLabels?.feb?.label_value || "Feb",
      screenLabels?.march?.label_value || "Mar",
      screenLabels?.april?.label_value || "Apr",
      screenLabels?.may?.label_value || "May",
      screenLabels?.june?.label_value || "Jun",
      screenLabels?.july?.label_value || "Jul",
      screenLabels?.aug?.label_value || "Aug",
      screenLabels?.sep?.label_value || "Sep",
      screenLabels?.oct?.label_value || "Oct",
      screenLabels?.nov?.label_value || "Nov",
      screenLabels?.dec?.label_value || "Dec",
    ];

    const weekday = weekdays[localDate.getDay()];
    const month = months[localDate.getMonth()];
    const day = localDate.getDate();
    const year = localDate.getFullYear();
    const hours = localDate.getHours();
    const minutes = localDate.getMinutes().toString().padStart(2, "0");
    const amPm = hours >= 12 ? "PM" : "AM";
    const hour12 = hours % 12 || 12;

    return `${weekday}, ${month} ${day}, ${year} | ${hour12}:${minutes} ${amPm}`;
  };

  const routeTitles = {
    "/dashboard": screenLabels?.dashboard?.label_value || "Dashboard",
    "/dashboard2": screenLabels?.dashboard?.label_value || "Dashboard",
    "/privacy-policy":
      screenLabels?.privacy_policy?.label_value || "Privacy Policy",
    "/terms-condition":
      screenLabels?.terms_conditions?.label_value || "Terms & Conditions",
    "/profile": screenLabels?.profile?.label_value || "Profile",
    "/profile-update":
      screenLabels?.update_profile?.label_value || "Update Profile",
    "/language-selection":
      authLabels?.select_language?.label_value || "Select Language",
    "/deleteAcc-one":
      screenLabels?.delete_account?.label_value || "Delete Account",
    "/help-guide": screenLabels?.help_and_guide?.label_value || "Help & Guide",
    "/change-password":
      screenLabels?.change_pass?.label_value || "Change Password",
    "/start-scan": screenLabels?.start_scan?.label_value || "Scan",
    "/history": screenLabels?.history?.label_value || "History",
    "/user-scan-details-four-life":
      screenLabels?.history?.label_value || "History",
    "/documents": screenLabels?.documents?.label_value || "Documents",
    "/view-more-details":
      screenLabels?.all_scan_details?.label_value || "All Scan Details",
    "/show-all-result":
      screenLabels?.all_scan_details?.label_value || "All Scan Details",
    "/show-all-result-2":
      screenLabels?.all_scan_details?.label_value || "All Scan Details",
    "/upload-document": storage.get("documentUpdate")
      ? screenLabels?.update_docs?.label_value || "Update Document"
      : screenLabels?.upload_docs?.label_value || "Upload Document",
    "/articles": screenLabels?.resources?.label_value || "Resources",
    "/user-scan-details": selectedDate
      ? formatDateTranslated(selectedDate, screenLabels)
      : screenLabels?.scan_details?.label_value || "Scan Details",
    "/about-us": screenLabels?.about_us?.label_value || "About Us",
    "/privacy-consent":
      screenLabels?.export_report?.label_value || "Export Report",
    "/export-document":
      screenLabels?.export_document?.label_value || "Export Document",
    "/view-export": screenLabels?.export_list?.label_value || "Export List",
    "/notification": screenLabels?.notification?.label_value || "Notification",
    "/guest-scan": screenLabels?.guest_scan?.label_value || "Guest Scan",
    "/guest-history":
      screenLabels?.guest_history?.label_value || "Guest History",
    "/guest-list": screenLabels?.guest_list?.label_value || "Guest List",
    "/survey-form": screenLabels?.survey_form?.label_value || "Survey Form",
    "/invites-list": screenLabels?.invitations?.label_value || "Invitations",
    "/invites-create": campaign
      ? screenLabels?.update_invite?.label_value || "Update Invite"
      : screenLabels?.create_campaign_btn?.label_value || "Create Invite",
    // "/invites-details": campaign?.name,
    "/invites-details":
      screenLabels?.invitation_details?.label_value || "Invitation Details",
    "/view-questions": screenLabels?.view_questions?.label_value || "Questions",
    "/ask-ai":
      screenLabels?.your_personal_health_assistant?.label_value ||
      "Your personal health assistant",
    "/charts-screen":
      screenLabels?.health_dashboard?.label_value || " Health Dashboard",
    "/recommended-product":
      screenLabels?.recommended_products?.label_value || "Recommended Products",
    "/product-details":
      screenLabels?.product_details?.label_value || "Product Details",
    "/web-scan": screenLabels?.user_scan?.label_value || "User Scan",
    "/mobile-scan-prompt":
      screenLabels?.mobile_only_title?.label_value || "Scan to Continue",
    "/scan-qr-code":
      screenLabels?.mobile_only_title?.label_value || "Scan to Continue",
    "/bp-calibration":
      screenLabels?.bp_calibration?.label_value || "Blood Pressure Calibration",
    "/plan-expired":
      screenLabels?.plan_expired_header?.label_value || "Plan Expired",
  };

  let pageTitle = routeTitles[route.pathname] || "App";
  if (currentPath.startsWith("/articles/")) {
    pageTitle = screenLabels?.details?.label_value || "Details";
  }

  const handleBack = () => {
    if (
      currentPath === "/deleteAcc-one" ||
      currentPath === "/change-password"
    ) {
      setIsVerified(false);
      navigate("/profile");
    } else if (currentPath === "/profile") {
      navigate(homeRoute);
    } else if (currentPath === "/show-all-result" && location.state?.scanId) {
      navigate("/user-scan-details-four-life", {
        state: {
          restoreSelectedScan: true,
          selectedScanKey: location.state.scanId,
        },
      });
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    const excludedRoutes = ["/change-password"];
    const wasExcluded = excludedRoutes.includes(previousPath.current);
    const isNowExcluded = excludedRoutes.includes(location.pathname);
    if (wasExcluded && !isNowExcluded) {
      storage.set("UserModule", "Login");
    }
    previousPath.current = location.pathname;

    if (location.pathname === "/notification") {
      setIsNotificationReceived(false);
      localStorage.setItem("notificationsSeen", "true");
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".hdr-profile-wrapper")) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const navItems = [
    {
      icon: <House size={16} />,
      label: screenLabels?.home?.label_value || "Home",
      link: homeRoute,
      matchPaths: ["/dashboard", "/dashboard2", "/view-more-details"],
    },
    {
      icon: <ScanFace size={16} />,
      label: screenLabels?.start_scan?.label_value || "Start Scan",
      link: "/start-scan",
      matchPaths: ["/start-scan"],
    },
    {
      icon: <ScanFace size={16} />,
      label: screenLabels?.guest_scan?.label_value || "Guest Scan",
      matchPaths: ["/guest-list", "/guest-scan"],
      onClick: () => {
        getGuestUserList();
      },
    },
    ...(!is4Life
      ? [
        {
          icon: <Newspaper size={16} />,
          label: screenLabels?.resources?.label_value || "Resources",
          link: "/articles",
          matchPaths: ["/articles"],
        },
      ]
      : []),
    // {
    //   icon: <ShoppingBag size={16} />,
    //   label:
    //     screenLabels?.recommended_products?.label_value ||
    //     "Recommended Products",
    //   link: "/recommended-product",
    //   matchPaths: ["/recommended-product", "/product-details"],
    // },
    ...(!is4Life
      ? [
        {
          icon: <Files size={16} />,
          label: screenLabels?.documents?.label_value || "Documents",
          link: "/documents",
          matchPaths: ["/documents", "/upload-document"],
        },
      ]
      : []),
    {
      icon: <History size={16} />,
      label: screenLabels?.history?.label_value || "History",
      link: is4Life ? "/user-scan-details-four-life" : "/history",
      matchPaths: [
        "/history",
        "/user-scan-details",
        "/user-scan-details",
        "/user-scan-details-four-life",
      ],
    },
    // ...(!is4Life ?
    //   [
    //     {
    //       icon: <HeartPulse size={16} />,
    //       label:
    //         screenLabels?.calibrate_bp?.label_value || "Calibrate Blood Pressure",
    //       link: "/bp-calibration",
    //     },
    //   ] : []),
    // {
    //   icon: <Bell size={16} />,
    //   label: screenLabels?.notification?.label_value || "Notification",
    //   link: "/notification",
    // },
    // ...(is4Life
    //   ? [
    {
      icon: <ScanQrCode size={16} />,
      label: screenLabels?.invites?.label_value || "Invites",
      link: isPlanActive ? "/invites-list" : "/plan-expired",
      matchPaths: [
        "/invites-list",
        "/invites-create",
        "/invites-details",
      ],
    },
    // ]
    // : []),
    // {
    //   icon: <ClipboardList size={16} />,
    //   label: screenLabels?.survey_form?.label_value || "Survey Form",
    //   link: "/survey-form",
    //   matchPaths: ["/survey-form"],
    // },
    {
      icon: <Info size={16} />,
      label: screenLabels?.about_us?.label_value || "About Us",
      link: "/about-us",
    },
    ...(!is4Life
      ? [
        {
          icon: <Bot size={16} />,
          label: screenLabels?.ask_ai?.label_value || "Ask AI",
          link: isPlanActive ? "/ask-ai" : "/plan-expired",
        },
      ]
      : []),
    {
      icon: <User size={16} />,
      label: screenLabels?.profile?.label_value || "Profile",
      link: "/profile",
      isActive: isProfileActive,
    },
    {
      icon: <ShieldAlert size={16} />,
      label: screenLabels?.privacy_policy?.label_value || "Privacy Policy",
      link: "/privacy-policy",
    },
    {
      icon: <ReceiptText size={16} />,
      label:
        screenLabels?.terms_conditions?.label_value || "Terms & Conditions",
      link: "/terms-condition",
    },
    {
      icon: null,
      label: screenLabels?.abt_us_2?.label_value || "App Version: 1.23",
      isVersion: true,
    },
  ];
  const isScanPage =
    currentPath === "/user-scan-details" ||
    currentPath === "/user-scan-details-four-life";
  const renderSidebarContent = () => (
    <div className="hdr-sidebar">
      <div
        className="hdr-sidebar-logo"
        style={process.env.REACT_APP_COMPANY_ID === "5tf4hl" ? { background: "#fff" } : undefined}
      >
        <img
          src={headerLogoUrl || logoUrl || images.appLogo}
          alt="app-logo"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = images.appLogo;
          }}
          className="mx-auto"
        />
      </div>

      <nav className="hdr-sidebar-nav">
        {navItems.map((item) => {
          if (item.isVersion) {
            return (
              <div key="app-version" className="hdr-nav-version">
                {/* {item.label} */}
              </div>
            );
          }

          const isActive = item.matchPaths
            ? item.matchPaths.some(
              (path) =>
                location.pathname === path ||
                location.pathname.startsWith(path + "/"),
            )
            : location.pathname === item.link;

          if (item.onClick) {
            return (
              <Link
                key={item.label}
                to={item.link}
                className={`hdr-nav-item${isActive ? " hdr-nav-active" : ""}`}
                onClick={(e) => {
                  setShow(false);
                  e.preventDefault();
                  item.onClick(navigate);
                }}
              >
                <span className="hdr-nav-icon">{item.icon}</span>
                {item.label}
              </Link>
            );
          }
          return (
            <Link
              key={item.label}
              to={item.link}
              className={`hdr-nav-item${isActive ? " hdr-nav-active" : ""}`}
              onClick={() => setShow(false)}
            >
              <span className="hdr-nav-icon">{item.icon}</span>
              {item.label}
            </Link>
          );
        })}
      </nav>
    </div>
  );

  const renderTopBar = (isDesktop = false) => (
    <div className={`hdr-topbar${isDesktop ? " hdr-topbar-desktop" : ""}`}>
      <div className="hdr-topbar-left">
        {isDashboard ? (
          !isDesktop && (
            <button
              className="hdr-icon-btn"
              onClick={() => setShow(true)}
              aria-label="Menu"
            >
              <AlignLeft size={20} />
            </button>
          )
        ) : (
          <button
            className="hdr-icon-btn hdr-back-btn"
            onClick={handleBack}
            aria-label="Back"
          >
            <ChevronLeft size={20} />
          </button>
        )}
        {isDashboard && isDesktop && <div style={{ width: 36 }} />}
      </div>

      <div className="hdr-topbar-center">
        {typeof pageTitle === "string" ? (
          <span className="hdr-page-title">{pageTitle}</span>
        ) : (
          pageTitle
        )}
      </div>

      <div className="hdr-topbar-right">
        {isScanPage && (
          <button
            className="hdr-icon-btn"
            onClick={() => setShowExportModal(true)}
            aria-label="Share"
          >
            <Share2 size={20} />
          </button>
        )}

        {!isScanPage && (
          <div
            className="hdr-profile-wrapper"
            onMouseEnter={() => {
              if (window.innerWidth >= 992) {
                setShowProfileMenu(true);
              }
            }}
          // onMouseLeave={() => {
          //   if (window.innerWidth >= 992) {
          //     setShowProfileMenu(false);
          //   }
          // }}
          >
            <div
              className="hdr-sidebar-avatar-wrap"
              onClick={() => {
                if (window.innerWidth < 992) {
                  setShowProfileMenu(!showProfileMenu);
                }
              }}
            >
              <img
                src={imagePath + (user?.profilePhoto || "") || images.userImage}
                alt="user"
                className="hdr-sidebar-avatar"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = images.userImage;
                }}
              />
              {isNotificationReceived && (
                <span className="hdr-notif-dot hdr-avatar-dot" />
              )}
            </div>

            {showProfileMenu && (
              <div className="hdr-profile-dropdown">
                <div className="hdr-dropdown-header">
                  <p className="hdr-signed-text">
                    {screenLabels?.signed?.label_value || "Signed in as"}
                  </p>
                  <p className="hdr-sidebar-name">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="hdr-sidebar-email">
                    {/* {user?.userEmail} */}
                    {is4Life ? user?.userSecondaryEmail : user?.userEmail}
                  </p>
                </div>

                <div className="hdr-dropdown-divider" />

                <button
                  className="hdr-dropdown-item"
                  onClick={() => {
                    navigate("/notification");
                    setShowProfileMenu(false);
                    handleNotificationClick();
                  }}
                >
                  <Bell size={16} />
                  {screenLabels?.notification?.label_value || "Notification"}
                  {isNotificationReceived && (
                    <span className="hdr-dropdown-badge" />
                  )}
                </button>

                <button
                  className="hdr-dropdown-item"
                  onClick={() => {
                    navigate("/profile");
                    setShowProfileMenu(false);
                  }}
                >
                  <User size={16} />
                  {screenLabels?.profile?.label_value || "Profile"}
                </button>

                {/* <button
                  className="hdr-dropdown-item logout"
                  onClick={() => {
                    setShowProfileMenu(false);
                    setShowLogoutModal(true);
                  }}
                >
                  <LogOut size={16} />
                  Log Out
                </button> */}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <header className="hdr-mobile d-lg-none">{renderTopBar(false)}</header>

      <header className="hdr-desktop d-none d-lg-flex">
        {renderTopBar(true)}
      </header>

      <Offcanvas
        show={show}
        onHide={() => setShow(false)}
        placement="start"
        className="hdr-offcanvas d-lg-none"
        style={{ width: "280px", border: "none" }}
      >
        <Offcanvas.Body className="p-0">
          <button
            className="hdr-offcanvas-close"
            onClick={() => setShow(false)}
            aria-label="Close menu"
          >
            <X size={16} />
          </button>
          {renderSidebarContent()}
        </Offcanvas.Body>
      </Offcanvas>

      <aside className="hdr-sidebar-fixed d-none d-lg-flex">
        {renderSidebarContent()}
      </aside>

      <LogOutModal
        show={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={() => {
          setShowLogoutModal(false);
          doLogout();
        }}
        screenLabels={screenLabels}
      />
    </>
  );
};

export default Header;
